import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/tilio";
import { useBooking, useLenis, scrollToId } from "../../context";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const lenis = useLenis();
    const openBooking = useBooking();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setMenuOpen(false);
        scrollToId(lenis, href);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled ? "border-b border-stone/70 bg-ivory/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[90px] max-w-6xl items-center justify-between px-6 lg:px-10">
                    <button
                        data-testid="header-logo"
                        onClick={() => go("#o-tilio")}
                        aria-label="Tilio – strona główna"
                        className="flex shrink-0 items-center gap-2.5 font-serif text-[32px] tracking-[-0.5px] text-pine"
                    >
                        <img
                            src="/images/tilio-domki-na-wynajem-mazowsze.png"
                            alt=""
                            aria-hidden="true"
                            width={229}
                            height={251}
                            loading="eager"
                            fetchPriority="high"
                            className="h-8 w-auto object-contain"
                        />
                        Tilio
                    </button>
                    <nav className="hidden items-center lg:flex lg:gap-5 xl:gap-8" data-testid="desktop-nav">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.href}
                                data-testid={`nav-link-${l.href.slice(1)}`}
                                onClick={() => go(l.href)}
                                className="link-underline text-[15px] font-medium tracking-normal text-sage transition-colors duration-300 hover:text-pine"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="header-availability-btn"
                            onClick={() => openBooking(null)}
                            className="btn-primary hidden whitespace-nowrap !px-6 !py-2.5 !text-[15px] sm:inline-flex"
                        >
                            Sprawdź dostępność i cennik
                        </button>
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setMenuOpen(true)}
                            className="flex h-10 w-10 items-center justify-center text-pine lg:hidden"
                            aria-label="Otwórz menu"
                        >
                            <Menu className="h-5 w-5" strokeWidth={1.25} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] flex flex-col bg-ivory px-6 py-5"
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-serif text-[32px] tracking-[-0.5px] text-pine">Tilio</span>
                            <button
                                data-testid="mobile-menu-close"
                                onClick={() => setMenuOpen(false)}
                                className="flex h-10 w-10 items-center justify-center text-pine"
                                aria-label="Zamknij menu"
                            >
                                <X className="h-5 w-5" strokeWidth={1.25} />
                            </button>
                        </div>
                        <nav className="mt-14 flex flex-col gap-2">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.href}
                                    data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    onClick={() => go(l.href)}
                                    className="border-b border-stone/70 py-4 text-left font-serif text-3xl tracking-tight text-pine"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                        </nav>
                        <button
                            data-testid="mobile-availability-btn"
                            onClick={() => { setMenuOpen(false); openBooking(null); }}
                            className="btn-primary mt-10 w-full"
                        >
                            Sprawdź dostępność i cennik
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
